import { CARCASS_MM } from "./carcass";
import { cellFrontLeaves, flapProblem, liftSpec, liftUnavailableReason, mezzanineFlapDirection, shelvingExcludedLifts } from "./flapFronts";
import { moduleHeadroomMm, shelvingCellHeadroomMm } from "./flapHeadroom";
import { moduleOverlapsFloor } from "./layout";
import type { PlannerProject, ValidationMessage } from "../types";
import { INTERNALS } from "../catalog";
import { cornerRailFrontClearanceMm, CORNER_HANGER_HALF_WIDTH_MM } from "./corner";
import { internalLayoutConflicts } from "./compartments";
import { normalizeSideMirrors, sideMirrorBlocker, sideMirrorSideName } from "./sideMirrors";
import { fitCornerItems } from "./cornerInternals";
import { cornerFillerShortages } from "./cornerHandles";
import { clothesFitBelowRail } from "./hanging";
import { topFillerMm } from "./installation";
import {
  usableRoomHeightMm,
  doorLeafCount,
  mezzanineFlapLayout,
  MAX_HINGED_DOOR_WIDTH_MM,
  MAX_MODULE_HEIGHT_MM,
  MAX_SLIDING_DOOR_WIDTH_MM,
  moduleTopMm,
  hasUnifiedFacade,
  unifiedFacadeHeightMm,
  STANDARD_FACADE_BOARD_LENGTH_MM
} from "./moduleRules";
import { cabinetDoorBaseMm } from "./cabinetDrawers";
import {
  getShelvingDiagonalSections,
  getShelvingGridLayout,
  shelvingExtraDividerMaxAngle,
  shelvingExtraDividerPosition,
  shelvingCellFits,
  shelvingMainDividerMaxAngle,
  SHELVING_MAX_CLEAR_SPAN_MM,
  getShelvingCellLayouts,
  getShelvingMergedCells,
  shelvingCellShelfYs,
  shelvingCellDividerXs,
  SHELVING_DRAWER_MIN_FRONT_MM,
  SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM,
  SHELVING_PANEL_THICKNESS_MM,
  gridCellWidth
} from "./shelving";
import {
  canAttachEndModule,
  endModuleDescription,
  endModuleForTargetSide,
  endModuleSide,
  endModuleTarget
} from "./endModules";
import { SHOE_SHELF_LIP_MM } from "./wardrobeFittings";

/** Сколько нужно от штанги до обувницы, чтобы пальто не ложилось на обувь. */
const SHOE_UNDER_RAIL_MIN_MM = 1200;

export const validateProject = (project: PlannerProject): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];

  if (!project.modules.length) {
    messages.push({
      id: "project-empty",
      level: "warning",
      text: "Добавьте хотя бы одну секцию"
    });
  }

  project.modules.forEach((module, index) => {
    const prefix = `Секция ${index + 1}`;

    // Крыша, дно и задник шкафа длиннее листа ЛДСП 2800 мм (проверка 07.10):
    // один корпус бывает шире листа — цех сращивает их над стойкой или делит корпус.
    if ((module.type === "single" || module.type === "double") &&
      module.width - CARCASS_MM * 2 > STANDARD_FACADE_BOARD_LENGTH_MM) {
      messages.push({
        id: `${module.id}-sheet-length`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: крыша, дно и задняя стенка ${Math.round(module.width - CARCASS_MM * 2)} мм длиннее листа ЛДСП ${STANDARD_FACADE_BOARD_LENGTH_MM} мм — на производстве их сращивают над стойкой или делят корпус; уточните заранее`
      });
    }

    // Изделия, стоящие внахлёст, не собрать. Раздвинуть их автоматически
    // не всегда возможно (нет места у стены) — тогда нужно сказать об этом
    // (проверка 07.10). Торцы проверяются ниже своим правилом.
    if (!module.stackedOnId && module.type !== "end" && moduleOverlapsFloor(project, module.id)) {
      messages.push({
        id: `${module.id}-floor-overlap`,
        moduleId: module.id,
        level: "error",
        text: `${prefix}: стоит внахлёст с соседним изделием — сдвиньте его, уменьшите ширину или увеличьте комнату`
      });
    }

    (normalizeSideMirrors(module) ?? []).forEach((side) => {
      const blocker = sideMirrorBlocker(project, module, side);
      if (!blocker) return;
      messages.push({
        id: `${module.id}-side-mirror-${side}`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: зеркало на боковине ${sideMirrorSideName(side)} закрыто ${blocker === "end" ? "торцевыми полками" : "соседним корпусом"} и в заказ не попадёт`
      });
    });

    if (hasUnifiedFacade(project, module)) {
      const height = unifiedFacadeHeightMm(project, module, cabinetDoorBaseMm(module));
      if (height > STANDARD_FACADE_BOARD_LENGTH_MM - 20) {
        messages.push({
          id: `${module.id}-unified-facade-height`,
          moduleId: module.id,
          level: "warning",
          text: `${prefix}: единая с антресолью створка ${height} мм длиннее обычной плиты ${STANDARD_FACADE_BOARD_LENGTH_MM} мм — уточните на производстве или сделайте фасад раздельным`
        });
      }
    }

    module.items
      .filter((item) => item.type === "drawer" && (item.height ?? 180) < SHELVING_DRAWER_MIN_FRONT_MM)
      .forEach((item) => {
        messages.push({
          id: `${module.id}-drawer-height-${item.id}`,
          moduleId: module.id,
          level: "warning",
          text: `${prefix}: ящик высотой ${item.height ?? 180} мм — меньше ${SHELVING_DRAWER_MIN_FRONT_MM} мм; направляющая и дно съедят полезную высоту. Удалите его и добавьте заново`
        });
      });

    // Совместимость наполнения с конструкцией: ничего не проходит сквозь
    // общую стойку или перегородку, и в каждом отсеке хватает ширины.
    internalLayoutConflicts(module).forEach((conflict) => {
      const item = module.items.find((candidate) => candidate.id === conflict.itemId);
      const name = item ? INTERNALS[item.type].name.toLowerCase() : "элемент";
      messages.push({
        id: `${module.id}-layout-${conflict.kind}-${conflict.itemId}`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: ${name} — ${conflict.text}`
      });
    });

    if (module.type === "shelving") {
      const grid = getShelvingGridLayout(module);
      const diagonalSections = getShelvingDiagonalSections(module, grid);
      if (moduleTopMm(project, module) > usableRoomHeightMm(project)) {
        messages.push({
          id: `${module.id}-ceiling`,
          moduleId: module.id,
          level: "error",
          text: `${prefix}: стеллаж входит в потолочную падугу`
        });
      }
      // Число колонок уже держит пролёт в норме, но отдельный проём можно
      // растянуть вручную — полка в нём прогнётся, и об этом надо сказать.
      // У рядов могут быть свои ширины (№ 19) — проверяем каждый ряд, проём называем один раз.
      const reportedSpans = new Set<string>();
      grid.rowCellWidths.forEach((widths, row) => widths.forEach((span, column) => {
        if (span <= SHELVING_MAX_CLEAR_SPAN_MM + 0.5) return;
        const key = grid.rowCustomColumns[row] ? `${column}-row-${row}` : `${column}`;
        if (reportedSpans.has(key)) return;
        reportedSpans.add(key);
        messages.push({
          id: `${module.id}-span-${key}`,
          moduleId: module.id,
          level: "warning",
          text: `${prefix}: проём ${column + 1}${grid.rowCustomColumns[row] ? ` в ряду ${row + 1}` : ""} шириной ${Math.round(span)} мм без промежуточной стойки. Полка длиннее ${SHELVING_MAX_CLEAR_SPAN_MM} мм со временем прогнётся — сузьте проём или добавьте колонку.`
        });
      }));
      const cellLayouts = new Map(getShelvingCellLayouts(module, grid).map((layout) => [layout.id, layout]));
      // Объединённый проём тоже не шире 1200 в свету: его могли объединить
      // в норме, а потом расширить стеллаж (проверка 07.10).
      getShelvingMergedCells(module, grid).forEach((section) => {
        if (section.columnSpan < 2 || section.width <= SHELVING_MAX_CLEAR_SPAN_MM + 0.5) return;
        messages.push({ id: `${section.cell.id}-merged-span`, moduleId: module.id, level: "warning",
          text: `${prefix}: объединённый проём ${section.cell.column + 1}×${section.cell.row + 1} шириной ${Math.round(section.width)} мм — полка или крыша над ним длиннее ${SHELVING_MAX_CLEAR_SPAN_MM} мм прогнётся. Разъедините ячейки или сузьте стеллаж.` });
      });
      // Распашная дверца ячейки шире 600 мм тяжела для петель (у шкафов это
      // предел створки) — сказать заранее (проверка 07.10).
      // Две дверцы или откидной фасад (09.10) этого предупреждения не требуют.
      (module.shelvingCells ?? []).forEach((cell) => {
        if (cell.content !== "door") return;
        const width = cellLayouts.get(cell.id)?.width ?? gridCellWidth(grid, cell.row, cell.column);
        const height = cellLayouts.get(cell.id)?.height ?? grid.cellHeights[cell.row] ?? 0;
        const headroom = shelvingCellHeadroomMm(project, module, cell.id, grid);
        const excluded = shelvingExcludedLifts(module);
        const front = cellFrontLeaves(cell, width, height, headroom, excluded);
        // Выбранный подъёмник сюда не подходит (сверху ячейка, мало места до потолка) — стоит другой (10.10).
        if (front.flap?.lift && cell.doorLift && front.flap.lift !== cell.doorLift) {
          messages.push({ id: `${cell.id}-lift-changed`, moduleId: module.id, level: "warning",
            text: `${prefix}: ячейка ${cell.column + 1}×${cell.row + 1}: ${liftUnavailableReason(cell.doorLift, height, headroom, excluded) ?? `${liftSpec(cell.doorLift).title} не подходит`}. Поставлен ${liftSpec(front.flap.lift).title}.` });
        }
        if ((cell.doorOpening === "up" || cell.doorOpening === "down") && !front.flap) {
          messages.push({ id: `${cell.id}-flap-height`, moduleId: module.id, level: "warning",
            text: `${prefix}: ячейка ${cell.column + 1}×${cell.row + 1}: ${flapProblem(cell.doorOpening, height, headroom, excluded)}. Пока стоит распашная дверца — выберите две дверцы или другое открывание.` });
          return;
        }
        if (front.leaves.length !== 1 || front.flap || width <= MAX_HINGED_DOOR_WIDTH_MM + 0.5) return;
        messages.push({ id: `${cell.id}-door-width`, moduleId: module.id, level: "warning",
          text: `${prefix}: дверца в ячейке ${cell.column + 1}×${cell.row + 1} шириной ${Math.round(width)} мм — шире ${MAX_HINGED_DOOR_WIDTH_MM} мм распашная створка тяжела для петель и провисает. Выберите «Две дверцы» или откидной фасад вверх или вниз.` });
      });
      // Деталь длиннее листа ЛДСП: крыша, дно, общая задняя стенка (проверка 07.10).
      const innerLength = Math.round(module.width - SHELVING_PANEL_THICKNESS_MM * 2);
      if (innerLength > STANDARD_FACADE_BOARD_LENGTH_MM) {
        messages.push({ id: `${module.id}-sheet-length`, moduleId: module.id, level: "warning",
          text: `${prefix}: крыша и дно стеллажа ${innerLength} мм длиннее листа ЛДСП ${STANDARD_FACADE_BOARD_LENGTH_MM} мм — их придётся сращивать; уточните на производстве или разделите стеллаж на два.` });
      }
      (module.shelvingCells ?? []).forEach((cell) => {
        const width = cellLayouts.get(cell.id)?.width ?? gridCellWidth(grid, cell.row, cell.column);
        const height = cellLayouts.get(cell.id)?.height ?? grid.cellHeights[cell.row] ?? 0;
        // Лишние полки и перегородки, которым не хватило места, не ставятся — об этом надо сказать.
        const expectedShelves = (cell.extraShelf ? 1 : 0) + (cell.moreShelves?.length ?? 0);
        const expectedDividers = (cell.extraDivider ? 1 : 0) + (cell.moreDividers?.length ?? 0);
        if (cell.content !== "drawers" && cell.content !== "diagonal" &&
          (shelvingCellShelfYs(cell, height).length < expectedShelves ||
            shelvingCellDividerXs(cell, width).length < expectedDividers)) {
          messages.push({ id: `${cell.id}-boards-fit`, moduleId: module.id, level: "warning",
            text: `${prefix}: в ячейке ${cell.column + 1}×${cell.row + 1} часть полок или перегородок не помещается — между ними нужно от 100 мм. Лишние в заказ не пойдут.` });
        }
        if (cell.extraDivider && shelvingExtraDividerPosition(width, cell.extraDividerPosition) === null) {
          messages.push({ id: `${cell.id}-divider-width`, moduleId: module.id, level: "error",
            text: `${prefix}: для перегородки в ячейке ${cell.column + 1}×${cell.row + 1} нужен проём от ${SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM * 2 + SHELVING_PANEL_THICKNESS_MM} мм. Увеличьте колонку или уберите перегородку.` });
        }
        if (
          cell.extraDivider &&
          cell.extraDividerMode === "angled" &&
          shelvingExtraDividerMaxAngle(width, module.depth, 0.5) < 3
        ) {
          messages.push({
            id: `${cell.id}-divider-angle`,
            moduleId: module.id,
            level: "error",
            text: `${prefix}: ячейка ${cell.column + 1}×${cell.row + 1} слишком узкая для поворотной стойки. Увеличьте колонку или выберите прямую стойку.`
          });
        }
        if (
          cell.column > 0 &&
          Math.abs(cell.leftDividerAngle ?? 0) >= 0.5 &&
          shelvingMainDividerMaxAngle(
            gridCellWidth(grid, cell.row, cell.column - 1),
            gridCellWidth(grid, cell.row, cell.column),
            module.depth
          ) < 3
        ) {
          messages.push({
            id: `${cell.id}-main-divider-angle`,
            moduleId: module.id,
            level: "error",
            text: `${prefix}: между ячейками ${cell.column}×${cell.row + 1} и ${cell.column + 1}×${cell.row + 1} недостаточно места для поворота основной стойки.`
          });
        }
        if (!shelvingCellFits(cell.content, width, height)) {
          messages.push({
            id: `${cell.id}-size`,
            moduleId: module.id,
            level: "warning",
            text: `${prefix}: ячейка ${cell.column + 1}×${cell.row + 1} слишком мала для выбранного наполнения`
          });
        }
        const diagonalSection = diagonalSections.find(
          (section) => section.cell.id === cell.id
        );
        if (
          diagonalSection &&
          (diagonalSection.width < 420 || diagonalSection.height < 500)
        ) {
          messages.push({
            id: `${cell.id}-diagonal-size`,
            moduleId: module.id,
            level: "warning",
            text: `${prefix}: диагональная секция слишком мала для выразительной ромбовидной решётки`
          });
        }
      });
      return;
    }

    if (module.type === "end") {
      if (moduleTopMm(project, module) > usableRoomHeightMm(project)) {
        messages.push({
          id: `${module.id}-ceiling`,
          moduleId: module.id,
          level: "error",
          text: `${prefix}: торцевой модуль входит в потолочную падугу`
        });
      }
      const target = endModuleTarget(project, module);
      if (!target) {
        messages.push({
          id: `${module.id}-end-position`,
          moduleId: module.id,
          level: "warning",
          text: `${prefix}: ${endModuleDescription(module)} нужно прикрепить к соседнему корпусу`
        });
      } else {
        if (!canAttachEndModule(project, module, target)) {
          messages.push({
            id: `${module.id}-end-overlap`,
            moduleId: module.id,
            level: "error",
            text: `${prefix}: торцевой модуль пересекается с соседним изделием; выберите другой корпус или сторону`
          });
        }
        const firstAtSide = endModuleForTargetSide(project, target.id, endModuleSide(module));
        if (firstAtSide && firstAtSide.id !== module.id) {
          messages.push({
            id: `${module.id}-end-duplicate`,
            moduleId: module.id,
            level: "error",
            text: `${prefix}: ${endModuleSide(module) === "left" ? "левый" : "правый"} торец у этого шкафа уже установлен`
          });
        }
      }
      return;
    }

    const flapDirection = mezzanineFlapDirection(module);
    const mezzanineFlaps = flapDirection ? mezzanineFlapLayout(module, moduleHeadroomMm(project, module)) : null;
    const chosenLift = module.doorLift;
    if (mezzanineFlaps?.[0]?.front.lift && chosenLift && mezzanineFlaps[0].front.lift !== chosenLift) {
      messages.push({
        id: `${module.id}-lift-changed`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: ${liftUnavailableReason(chosenLift, mezzanineFlaps[0].heightMm, moduleHeadroomMm(project, module)) ?? `${liftSpec(chosenLift).title} не подходит`}. Поставлен ${liftSpec(mezzanineFlaps[0].front.lift).title}.`
      });
    }
    if (flapDirection && flapProblem(flapDirection, Math.max(220, module.height - 8))) {
      messages.push({
        id: `${module.id}-flap-height`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: ${flapProblem(flapDirection, Math.max(220, module.height - 8))}. Пока стоят распашные двери.`
      });
    }
    if (
      module.door === "hinged" &&
      module.type !== "corner45" &&
      !mezzanineFlapLayout(module) &&
      module.width > doorLeafCount(module) * MAX_HINGED_DOOR_WIDTH_MM
    ) {
      messages.push({
        id: `${module.id}-hinged-width`,
        moduleId: module.id,
        level: "error",
        text: `${prefix}: ширина распашной створки должна быть не больше 600 мм`
      });
    }

    if (
      module.door === "sliding" &&
      module.width / 2 > MAX_SLIDING_DOOR_WIDTH_MM
    ) {
      messages.push({
        id: `${module.id}-sliding-width`,
        moduleId: module.id,
        level: "error",
        text: `${prefix}: ширина одной раздвижной двери должна быть не больше 1200 мм`
      });
    }

    if (module.height > MAX_MODULE_HEIGHT_MM) {
      messages.push({
        id: `${module.id}-height`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: рекомендуем разделить корпус и добавить антресоль`
      });
    }

    if (moduleTopMm(project, module) > usableRoomHeightMm(project)) {
      messages.push({
        id: `${module.id}-ceiling`,
        moduleId: module.id,
        level: "error",
        text: topFillerMm(module)
          ? `${prefix}: корпус вместе с верхним добором входит в потолочную падугу`
          : `${prefix}: модуль входит в потолочную падугу`
      });
    }

    if (module.depth < 350 || module.depth > 580) {
      messages.push({
        id: `${module.id}-depth`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: базовая глубина производства — 350–580 мм`
      });
    }

    const hasShoeRack = module.items.some((item) => item.type === "shoeRack");
    if (module.type === "corner45" && !fitCornerItems(module)) {
      messages.push({ id: `${module.id}-corner-filling`, moduleId: module.id, level: "error",
        text: `${prefix}: наполнению не хватает высоты; уберите часть полок или увеличьте корпус` });
    }
    const rails = module.items.filter((item) => item.type === "rail");
    if (module.type === "corner45" && rails.length && cornerRailFrontClearanceMm(module) < CORNER_HANGER_HALF_WIDTH_MM) {
      messages.push({ id: `${module.id}-corner-rail-front`, moduleId: module.id, level: "warning",
        text: `${prefix}: плечики на штанге упрутся в дверь — от оси штанги до двери ${cornerRailFrontClearanceMm(module)} мм, нужно от ${CORNER_HANGER_HALF_WIDTH_MM}. Увеличьте глубину шкафа.` });
    }

    // Угловой (31.10): петли обеих дверей у угла, открытые двери ложатся вдоль
    // фасадов друг друга — доборы не уже толщины двери и выноса ручки.
    if (module.type === "corner45" && module.door === "hinged") {
      for (const shortage of cornerFillerShortages(project, module)) {
        messages.push({ id: `${module.id}-corner-filler-${shortage.which}`, moduleId: module.id, level: "warning",
          text: shortage.which === "corner"
            ? `${prefix}: открытая дверь углового может задеть дверь или ручку соседнего шкафа — добор углового нужен не меньше ${shortage.needed} мм (сейчас ${shortage.current}): толщина двери и вынос ручки.`
            : `${prefix}: открытая дверь соседнего шкафа может задеть дверь или ручку углового — добор соседа нужен не меньше ${shortage.needed} мм (сейчас ${shortage.current}): толщина двери и вынос ручки.` });
      }
    }

    // Обувница под штангой: длинная одежда ляжет на обувь. Конкретно — сколько
    // остаётся от штанги до обувницы (приёмка 04.10, wf-13).
    const shoeUnderRail = module.items
      .filter((item) => item.type === "shoeRack")
      .flatMap((shoe) => rails
        .filter((rail) => rail.y > shoe.y)
        .map((rail) => ({ shoe, rail, gap: Math.round(rail.y - shoe.y - SHOE_SHELF_LIP_MM) })))
      .find(({ gap }) => gap < SHOE_UNDER_RAIL_MIN_MM);
    if (hasShoeRack && shoeUnderRail) {
      messages.push({
        id: `${module.id}-shoe-rail`,
        moduleId: module.id,
        level: "warning",
        text: `${prefix}: от штанги до обувницы ${shoeUnderRail.gap} мм — длинная одежда ляжет на обувь. Поднимите штангу или опустите обувницу (нужно от ${SHOE_UNDER_RAIL_MIN_MM} мм)`
      });
    }

    rails.forEach((rail) => {
      if (!clothesFitBelowRail(module, rail)) {
        messages.push({
          id: `${rail.id}-clothes`,
          moduleId: module.id,
          level: "warning",
          text: `${prefix}: одежда скрыта — под штангой недостаточно высоты`
        });
      }
    });
  });

  return messages;
};
